import CMSChildrenContainer from '@/cmsComponents/ChildrenContainer';
import cx from 'classnames';
import Image from 'next/image';
import React from 'react';
import { CoverModule, ISingleMediaFile } from 'types/cms';

import { Theme, useMediaQuery } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

import { defaultAlt } from 'components/Image';

import { CoverStyle, FontColour, FontSize } from './coverTypes';
import styles from './style.module.scss';

const defaultUrl =
  'https://medworld-cdn-dev.s3.ap-southeast-2.amazonaws.com/medworld-app/smiling-female-doctor-default.webp';

export type SingleCoverComponentProps = Pick<
  CoverModule,
  'cover_collection' | 'layout' | 'font_size' | 'alignment' | 'font_colour'
>;

export const SingleCoverComponent = ({
  cover_collection,
  layout,
  font_size,
  alignment,
  font_colour = FontColour.Light,
}: SingleCoverComponentProps) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  if (!cover_collection) {
    // Don't render the SingleCoverComponent if the cover collection is missing.
    // This is expected, some CoverCollections in the CMS don't have all this data in them.
    return <></>;
  }

  const {
    title,
    button_text,
    button_URL,
    hyper_link,
    hyper_link_text,
    button_style = 'Default',
  } = cover_collection;

  const image =
    cover_collection.image ??
    ({
      url: defaultUrl,
      alternativeText: defaultAlt,
    } as ISingleMediaFile);

  return (
    <div className={styles.container}>
      {isDesktop && (
        <div className={styles.bgImageWrapper}>
          {image && (
            <Image
              src={image.url ?? defaultUrl}
              layout="fill"
              className={styles.bgImage}
              alt=""
              priority={true}
            />
          )}
        </div>
      )}
      <div className={styles.coverLayout}>
        <CMSChildrenContainer>
          <div
            className={cx(
              styles.content,
              styles[alignment],
              styles[font_colour || FontColour.Light],
              styles[layout],
            )}
          >
            {title && (
              <Typography
                variant={font_size === FontSize.Large ? 'h4' : 'h5'}
                className={styles.title}
                component="h1"
              >
                {title}
              </Typography>
            )}
            {(button_URL || hyper_link) && (
              <div
                className={cx(
                  alignment === CoverStyle.Center ? styles.linkCenter : '',
                  styles.buttonContainer,
                )}
              >
                {button_URL && (
                  <div className={styles.button}>
                    <Button
                      variant="contained"
                      color="secondary"
                      href={button_URL}
                      className={cx(styles.button, styles[button_style])}
                    >
                      <span className={styles.linkText}>{button_text}</span>
                    </Button>
                  </div>
                )}
                {hyper_link && (
                  <Button href={hyper_link} className={styles.hyperLink}>
                    <span className={styles[font_colour]}>{hyper_link_text}</span>
                    <ArrowForwardIcon className={styles[font_colour]} />
                  </Button>
                )}
              </div>
            )}
          </div>
        </CMSChildrenContainer>
      </div>
    </div>
  );
};

export default SingleCoverComponent;
